import React, { useState } from 'react';
import { useAuth } from '../Auth/AuthContext';
import { useLocation, useNavigate } from 'react-router-dom';
import './Login.css';

export default function Login() {
    const { login } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const [form, setForm] = useState({ email: '', password: '', remember: true });
    const [error, setError] = useState('');
    const [submitting, setSubmitting] = useState(false);

    const from = location.state?.from?.pathname || sessionStorage.getItem('redirectAfterLogin') || '/';

    const onChange = (e) => {
        const { name, type, checked, value } = e.target;
        setForm((f) => ({ ...f, [name]: type === 'checkbox' ? checked : value }));
    };

    const onSubmit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        setError('');
        try {
            await login(form);
            sessionStorage.removeItem('redirectAfterLogin');
            navigate(from, { replace: true });
        } catch (err) {
            const status = err?.response?.status;
            if (status === 422) {
                const msg = err?.response?.data?.message || 'Invalid input.';
                setError(msg);
            } else if (status === 429) {
                setError('Too many attempts. Please try again shortly.');
            } else if (status === 419) {
                setError('Session expired. Please retry.');
            } else {
                setError('Login failed. Check your credentials and try again.');
            }
        } finally {
            setSubmitting(false);
        }
    };

    // You can swap this for a local asset later (e.g., /images/login-art.jpg)
    const IMAGE_URL =
        '/assets/images/pms-login.png';
        //'https://images.unsplash.com/photo-1520975916090-3105956dac38?q=80&amp;auto=format&amp;fit=crop&amp;w=1400&amp;h=1600';


    return (
        <div className="login-page container-fluid py-4">
            <div className="login-card mx-auto bg-white">
                <div className="row g-0">
                    {/* LEFT: Artwork + quote */}
                    <div className="d-none d-md-block col-md-7 position-relative login-art">
                        <img src={IMAGE_URL} alt="" className="login-art-img" />
                        <div className="login-art-overlay d-flex flex-column justify-content-end p-4">
                            <div className="text-uppercase small opacity-75 mb-2 login-quote-kicker">
                                A WISE QUOTE
                            </div>
                            <h2 className="display-6 fw-semibold mb-3 login-quote-title">
                                Get <br /> Everything <br /> You Want
                            </h2>
                            <p className="small mb-2 opacity-75">
                                You can get everything you want if you work hard, trust the process, and stick to the plan.
                            </p>
                        </div>
                    </div>

                    {/* RIGHT: Form */}
                    <div className="col-12 col-md-5"><div className="login-form-wrap h-100 d-flex flex-column justify-content-center p-4 p-md-5">
                        {/* Brand / Logo */}
                        <div className="text-center mb-3">
                            <span className="login-brand d-inline-flex align-items-center justify-content-center rounded-circle">
                                {/* Simple glyph */}
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                                    <circle cx="12" cy="12" r="9" stroke="#111" strokeWidth="1.5" />
                                    <path d="M6 12h12M12 6v12" stroke="#111" strokeWidth="1.5" />
                                </svg>
                            </span>
                        </div>

                        <h1 className="text-center login-title">
                            Welcome Back
                        </h1>
                        <p className="text-center text-muted mb-4">
                            Enter your email and password to access your account
                        </p>

                        {error && (
                            <div className="alert alert-danger py-2" role="alert">
                                {error}
                            </div>
                        )}

                        <form onSubmit={onSubmit} className="login-form">
                            <div className="mb-3">
                                <label className="form-label" htmlFor="email">Email</label>
                                <input
                                    id="email"
                                    name="email"
                                    type="email"
                                    className="form-control login-input"
                                    value={form.email}
                                    onChange={onChange}
                                    required
                                    autoFocus
                                />
                            </div>

                            <div className="mb-2">
                                <label className="form-label" htmlFor="password">Password</label>
                                <div className="position-relative">
                                    <input
                                        id="password"
                                        name="password"
                                        type="password"
                                        className="form-control login-input"
                                        value={form.password}
                                        onChange={onChange}
                                        required
                                    />
                                    {/* (Optional) eye icon placeholder */}
                                    <span className="password-eye" aria-hidden="true">👁️</span>
                                </div>
                            </div>

                            <div className="d-flex align-items-center justify-content-between mb-3">
                                <div className="form-check">
                                    <input
                                        className="form-check-input"
                                        type="checkbox"
                                        id="remember"
                                        name="remember"
                                        checked={form.remember}
                                        onChange={onChange}
                                    />
                                    <label className="form-check-label" htmlFor="remember">
                                        Remember me
                                    </label>
                                </div>
                                <a href="#" className="small text-muted text-decoration-none">Forgot Password</a>
                            </div>

                            <button type="submit" className="btn btn-dark w-100 login-primary-btn" disabled={submitting}>
                                {submitting ? 'Signing in…' : 'Sign In'}
                            </button>

                            {/* Divider */}
                            <div className="login-divider text-center text-muted">or</div>

                            {/* Google button (non-functional stub) */}
                            <button
                                type="button"
                                className="btn w-100 login-google-btn"
                                onClick={() => {}}
                            >

                                <span className="d-inline-flex align-items-center justify-content-center me-2" aria-hidden="true">
                                    <svg xmlns="http://www.w3.org/2000/svg"
                                         width="18" height="18"
                                         shapeRendering="geometricPrecision"
                                         textRendering="geometricPrecision"
                                         imageRendering="optimizeQuality"
                                         fillRule="evenodd"
                                         clipRule="evenodd"
                                         viewBox="0 0 640 640">
                                        <path d="M.2 298.669L0 90.615l256.007-34.76v242.814H.201zM298.658 49.654L639.905-.012v298.681H298.657V49.654zM640 341.331l-.071 298.681L298.669 592V341.332h341.33zM255.983 586.543L.189 551.463v-210.18h255.794v245.26z"/>
                                    </svg>
                                    <span className='ms-2'>Sign in with Microsoft</span>
                                </span>

                            </button>
                        </form>

                        <p className="text-center mt-3 mb-0">
                            <a href="/" className="link-secondary text-decoration-none">Back to Home</a>
                        </p>
                    </div></div>
                </div>
            </div>
        </div>
    );
}
